/**
 * src/pages/Home.jsx
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ProductCard } from "../components/ProductCard";
import { HeroCarousel } from "../components/HeroCarousel";
import { InstagramSection } from "../components/InstagramSection";
import { fetchBestsellers, fetchCollectionTiles } from "../lib/supabaseProducts";
import { slugify } from "../lib/slug";

const REVIEWS = [
  { name: "Hargobind Mamgai", when: "2 months ago", rating: 4, text: "I had a great experience at Ritu Fashion Store. They have a beautiful collection of trendy and quality clothing at reasonable prices" },
  { name: "Tamanna Yadav", when: "6 years ago", rating: 5, text: "Best prize with best quality clothes provided. With latest collection." },
  { name: "Anjali Gupta", when: "3 weeks ago", rating: 4, text: "We bought party wear for my son's birthday. The fabric is soft and the fit was perfect." },
  { name: "Pooja Sharma", when: "2 months ago", rating: 5, text: "Ordered a cotton co-ord set on Website and it arrived in two days. Lovely quality for the price." },
  { name: "Rahul Verma", when: "1 month ago", rating: 5, text: "A proper family store — they helped us pick matching festive outfits for both kids." },
];

function Stars({ count }) {
  return (
    <div className="review-stars">
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} style={{ color: i < count ? "#f5a623" : "var(--border)", fontSize: 14 }}>★</span>
      ))}
    </div>
  );
}

export default function Home() {
  const [bestsellers, setBestsellers] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchBestsellers(8), fetchCollectionTiles(4)]).then(([b, c]) => {
      setBestsellers(b);
      setCollections(c);
      setLoading(false);
    });
  }, []);

  return (
    <div>
      <Helmet>
        <title>Ritu Fashion Store | Kids wear in Gurgaon</title>
        <meta
          name="description"
          content="Handpicked cotton dresses, co-ord sets and festive kurtas for kids. Shop Ritu Fashion Store, Sadar Bazaar Gurgaon, with free shipping on prepaid orders."
        />
        <meta property="og:title" content="Ritu Fashion Store | Kids wear" />
        <meta property="og:description" content="Cotton dresses, co-ords and festive sets for little ones, from Gurgaon." />
      </Helmet>

      {/* Hero */}
      <HeroCarousel />

      {/* Trust bar */}
       <section className="trust-bar">
              <div className="container trust-grid">
                <div className="trust-item">
                  <span style={{ fontSize: 24 }}>🌿</span>
                  <div>
                    <p>Gentle on Skin</p>
                    <p>Soft, breathable fabric for sensitive skin</p>
                  </div>
                </div>
                <div className="trust-item">
                  <span style={{ fontSize: 24 }}>🚚</span>
                  <div>
                    <p>1-day delivery in Gurgaon</p>
                    <p>Dispatched same day, everywhere else in 24 hours</p>
                  </div>
                </div>
                <div className="trust-item">
                  <span style={{ fontSize: 24 }}>🏠️</span>
                  <div>
                    <p>Loved in Gurgaon</p>
                    <p>A family-run store since 1996</p>
                  </div>
                </div>
              </div>
            </section>

      {/* Bestsellers */}
      {!loading && bestsellers.length > 0 && (
        <section className="section-primary">
          <div className="container">
            <h2>Bestsellers</h2>
            <div className="product-grid mt-8">
              {bestsellers.map((p) => (
                <ProductCard key={p.productId} product={p} tone="onPrimary" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Collections */}
      {!loading && collections.length > 0 && (
        <section className="section">
          <div className="container">
            <h2 className="section-title">Collections</h2>
            <div className="collection-grid mt-8">
              {collections.map((c) => (
                <Link
                  key={`${c.gender}-${c.category}`}
                  to={`/shop/${c.gender}/${slugify(c.category)}`}
                  className="collection-item"
                >
                  <div className="collection-item-img">
                    <img src={c.image} alt={c.label} loading="lazy" width={900} height={1100} />
                  </div>
                  <p className="collection-item-label">
                    {c.label} <span>→</span>
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Instagram */}
      <InstagramSection />

      {/* Reviews marquee */}
      <section className="section" style={{ overflow: "hidden" }}>
        <h2 className="section-title text-center">What Our Customers Say</h2>
        <div className="reviews-viewport">
          <div className="reviews-track">
            {[...REVIEWS, ...REVIEWS].map((r, i) => (
              <figure key={`${r.name}-${i}`} className="review-card">
                <div className="review-head">
                  <span className="review-avatar">{r.name[0]}</span>
                  <div style={{ minWidth: 0 }}>
                    <figcaption className="review-name">{r.name}</figcaption>
                    <p className="review-when">{r.when}</p>
                  </div>
                </div>
                <Stars count={r.rating} />
                <blockquote className="review-text">"{r.text}"</blockquote>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / visit */}
      <section className="section-primary">
        <div className="container">
          <div style={{ maxWidth: "42rem" }}>
            <h2>Questions while you shop? Just reach out</h2>
            <p style={{ marginTop: 12, color: "var(--primary-foreground)", opacity: 0.85 }}>
              Need help with sizing, an order, or a custom festive outfit? Call or WhatsApp us any time — and if
              you're in Gurgaon, drop by our family store in Sadar Bazar and see the fabrics in person.
            </p>
          </div>
          <div className="contact-grid">
            <div className="contact-card">
              <span style={{ fontSize: 22 }}>📞</span>
              <p>Call or WhatsApp</p>
              <p className="muted" style={{ fontSize: 14 }}>
                Quick answers on products, sizes and delivery while you shop online.
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                <a href="tel:+919873564717" className="btn btn-outline btn-sm">+91 98735 64717</a>
                <a href="https://wa.me/919873804717" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
                  💬 Chat on WhatsApp
                </a>
              </div>
            </div>
            <div className="contact-card">
              <span style={{ fontSize: 22 }}>📍</span>
              <p>Visit our store in Gurgaon</p>
              <p className="muted" style={{ fontSize: 14 }}>
                157/13, Sadar Bazar Rd, near Subzi Mandi Road, Holi Ground, Roshan Pura, Gurugram, Haryana 122007
              </p>
              <p className="muted" style={{ fontSize: 14, marginTop: 10 }}>🕐 Open Mon – Sun, 10:00 AM – 9:00 PM</p>
              <a
                href="https://www.google.com/maps/search/?api=1&query=157%2F13%20Sadar%20Bazar%20Rd%20Roshan%20Pura%20Gurugram%20Haryana%20122007"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline btn-sm mt-4"
              >
                Get directions →
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}